import { useState, useEffect, useCallback } from 'react';
import { Plus, ShieldCheck, X } from 'lucide-react';
import { Fab } from '@/components/ui/Fab';

import {
  fetchCertificados,
  toggleCertificadoAtivo,
  type Certificado,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { notifyMutationError } from '../lib/errors';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PageHeader } from '../components/ui';
import { useNotification } from '../context/NotificationProvider';
import {
  CertificateUploadCard,
  CertificateList,
  ConectarPdpjCard,
} from '../components/certificates';

function CertificatesPage() {
  const { refreshUser } = useAuth();
  const { showSuccess, showError } = useNotification();

  // Certificates list state
  const [certificados, setCertificados] = useState<Certificado[]>([]);
  const [loading, setLoading] = useState(true);

  // Upload form visibility
  const [showForm, setShowForm] = useState(false);

  const loadCertificados = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchCertificados();
      setCertificados(data);
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadCertificados();
  }, [loadCertificados]);

  const handleToggle = async (cert: Certificado) => {
    try {
      const newAtivo = !cert.ativo;
      const updated = await toggleCertificadoAtivo(cert.numero_serie, newAtivo);
      setCertificados((prev) =>
        prev.map((c) => (c.numero_serie === updated.numero_serie ? updated : c))
      );
      // A validade do ativo vive no /auth/me (faixa de vencimento no topo).
      void refreshUser().catch(() => {});
      const label = cert.apelido
        ? `Certificado "${cert.apelido}"`
        : `Certificado de "${cert.nome || cert.numero_serie}"`;
      showSuccess(newAtivo ? `${label} ativado.` : `${label} desativado.`);
    } catch (err) {
      notifyMutationError(err, showError);
    }
  };

  const handleApelidoChange = (numeroSerie: string, apelido: string | null) => {
    setCertificados((prev) =>
      prev.map((c) => (c.numero_serie === numeroSerie ? { ...c, apelido } : c))
    );
  };

  const handleUploadSuccess = async () => {
    setShowForm(false);
    await loadCertificados();
  };

  const temCertificado = certificados.length > 0;
  // Sem certificado o cadastro aparece sozinho; com certificado, sob demanda.
  const mostrarCadastro = showForm || (!loading && !temCertificado);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        icon={<ShieldCheck />}
        title="Certificados e autenticação"
        subtitle="O certificado assina; a conexão com a PDPJ autentica."
      />

      {!showForm && temCertificado && (
        <Fab icon={<Plus className="h-6 w-6" />} label="Incluir novo certificado" onClick={() => setShowForm(true)} />
      )}

      {/* Seção 1 — certificados digitais A1 */}
      <section className="space-y-3" aria-labelledby="secao-certificados">
        <div className="flex min-h-9 items-center justify-between gap-2 px-1">
          <h2 id="secao-certificados" className="flex items-center gap-2 text-base font-semibold">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            Certificados digitais
          </h2>
          {temCertificado && (
            <Button
              variant={showForm ? 'ghost' : 'outline'}
              size="sm"
              className={showForm ? 'h-11 gap-1.5 sm:h-9' : 'hidden gap-1.5 sm:inline-flex'}
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {showForm ? 'Fechar' : 'Incluir novo'}
            </Button>
          )}
        </div>

        {mostrarCadastro && (
          <Card>
            <CardContent className="p-4 sm:p-5">
              <CertificateUploadCard onSuccess={handleUploadSuccess} />
            </CardContent>
          </Card>
        )}

        {/* Sem certificado, o cadastro já está na tela: o estado vazio só repetiria. */}
        {(loading || temCertificado) && (
          <CertificateList
            certificates={certificados}
            loading={loading}
            onToggle={handleToggle}
            onRefresh={loadCertificados}
            onApelidoChange={handleApelidoChange}
          />
        )}
      </section>

      {/* Seção 2 — conexão com a PDPJ (segundo fator OTP). Só com certificado
          ativo: é com ele que o JurisApp entra na PDPJ. */}
      {!loading && certificados.some((c) => c.ativo) && <ConectarPdpjCard />}
    </div>
  );
}

export default CertificatesPage;
